import { createSlice } from '@reduxjs/toolkit';
import {
  getBundle,
  getBundleCompanies,
  getBundleListing,
  getStructure,
  getModuleListing,
} from "./bundles.action";

// bundleId stamps the count with the bundle it was actually fetched for. Without it a slow
// response for bundle A could land after the admin has opened bundle B and be read as B's
// blast radius; the dialog compares the stamp before showing anything.
const noCompanies = { bundleId: null, total: 0, records: [] };

const BUNDLES = 'bundles';
const initialState = {
  listingTable: {
    totalRecords: 0,
    records: [],
  },
  bundleSelected: null,
  bundleCompanies: noCompanies,
  structure: [],
  moduleListing: [],
  // Same reason as companies.bundleOptionsLoaded: nothing tracks getModuleListing's loading,
  // so an empty catalogue is otherwise indistinguishable from one that has not arrived. The
  // company bundle editor needs both catalogues before it may say anything at all.
  moduleListingLoaded: false,
  // The catalogue read failed and nothing has ever come back. Loaded-vs-not cannot express
  // that on its own, and a panel that cannot tell "not back yet" from "it failed" shows a
  // skeleton for ever. Only ever read together with moduleListingLoaded: once a list HAS
  // landed, a later failed refresh leaves the panel on the cached one rather than blanking
  // a screen that is still perfectly usable.
  moduleListingFailed: false,
  isLoading: false,
  // The listing read was rejected and nothing is on screen to fall back to. isLoading alone
  // cannot express it — a rejected fetch that never reset the flag would leave the table
  // spinning for ever — so the page reads this to raise an inline Retry panel instead.
  listingFailed: false,
};
const bundlesSlice = createSlice({
  name: BUNDLES,
  initialState,
  reducers: {
    clear: (state, _) => {
      state.listingTable.totalRecords = 0;
      state.listingTable.records = [];
    },
    setPage: (state, action) => {
      state.listingTable.page = action.payload;
    },
    setItemPerPage: (state, action) => {
      state.listingTable.rowsPerPage = action.payload;
    },
    clearSelected: (state, _) => {
      state.bundleSelected = null;
      state.bundleCompanies = noCompanies;
    },
  },
  extraReducers: {
    [getBundleListing.pending]: (state, _) => {
      state.isLoading = true;
      state.listingFailed = false;
    },
    [getBundleListing.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.listingFailed = false;
      state.listingTable.records = action.payload.records;
      state.listingTable.totalRecords = action.payload.totalRecords;
    },
    [getBundleListing.rejected]: (state, _) => {
      state.isLoading = false;
      state.listingFailed = true;
    },
    [getBundle.pending]: (state, _) => {
      state.isLoading = true;
      state.bundleSelected = null;
    },
    [getBundle.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.bundleSelected = action.payload;
    },
    [getBundle.rejected]: (state, _) => {
      state.isLoading = false;
    },
    [getBundleCompanies.pending]: (state, action) => {
      state.bundleCompanies = { ...noCompanies, bundleId: action.meta.arg.id };
    },
    [getBundleCompanies.fulfilled]: (state, action) => {
      state.bundleCompanies = {
        bundleId: action.meta.arg.id,
        total: action.payload.total,
        records: action.payload.records,
      };
    },
    [getBundleCompanies.rejected]: (state, _) => {
      state.bundleCompanies = noCompanies;
    },
    [getStructure.fulfilled]: (state, action) => {
      state.structure = action.payload;
    },
    [getModuleListing.pending]: (state) => {
      state.moduleListingFailed = false;
    },
    [getModuleListing.fulfilled]: (state, action) => {
      state.moduleListing = action.payload;
      state.moduleListingLoaded = true;
      state.moduleListingFailed = false;
    },
    [getModuleListing.rejected]: (state) => {
      state.moduleListingFailed = true;
    },
  },
});

export const {
  clear,
  setPage,
  setItemPerPage,
  clearSelected,
} = bundlesSlice.actions;
export const bundlesReducer = bundlesSlice.reducer;
